import { useState, useEffect } from 'react';
import { useAuth } from '@/lib/auth-context';
import { branchAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Building2, Clock, MapPin } from 'lucide-react';

export default function BranchesPage() {
  const { isSuperUser } = useAuth();
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editBranch, setEditBranch] = useState(null);
  const [form, setForm] = useState({
    name: '', location: '', timezone: 'Asia/Kolkata',
    office_start_time: '09:30', grace_minutes: 20, lates_per_day_deduction: 3
  });

  useEffect(() => { loadBranches(); }, []);

  const loadBranches = async () => {
    try {
      const res = await branchAPI.list();
      setBranches(res.data);
    } catch { toast.error('Failed to load branches'); }
    finally { setLoading(false); }
  };

  const handleSubmit = async () => {
    if (!form.name || !form.location) {
      toast.error('Name and location are required');
      return;
    }
    try {
      if (editBranch) {
        await branchAPI.update(editBranch.id, form);
        toast.success('Branch updated');
      } else {
        await branchAPI.create(form);
        toast.success('Branch created');
      }
      setDialogOpen(false);
      setEditBranch(null);
      setForm({ name: '', location: '', timezone: 'Asia/Kolkata', office_start_time: '09:30', grace_minutes: 20, lates_per_day_deduction: 3 });
      loadBranches();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEdit = (branch) => {
    setEditBranch(branch);
    setForm({
      name: branch.name, location: branch.location, timezone: branch.timezone,
      office_start_time: branch.office_start_time, grace_minutes: branch.grace_minutes,
      lates_per_day_deduction: branch.lates_per_day_deduction
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this branch?')) return;
    try {
      await branchAPI.delete(id);
      toast.success('Branch deleted');
      loadBranches();
    } catch { toast.error('Delete failed'); }
  };

  const openNew = () => {
    setEditBranch(null);
    setForm({ name: '', location: '', timezone: 'Asia/Kolkata', office_start_time: '09:30', grace_minutes: 20, lates_per_day_deduction: 3 });
    setDialogOpen(true);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-semibold tracking-tight">Branches</h1>
          <p className="text-muted-foreground">Manage office locations and settings</p>
        </div>
        {isSuperUser && (
          <Button onClick={openNew} className="gap-2" data-testid="create-branch-button">
            <Plus size={16} /> Add Branch
          </Button>
        )}
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1,2].map(i => <div key={i} className="h-20 bg-muted animate-pulse rounded-xl" />)}
        </div>
      ) : branches.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">No branches yet. Create your first branch.</CardContent></Card>
      ) : (
        <div className="grid gap-4">
          {branches.map(branch => (
            <Card key={branch.id} className="transition-colors duration-150 hover:border-primary/20">
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Building2 size={20} className="text-primary" />
                      </div>
                      <div>
                        <h3 className="font-heading font-semibold">{branch.name}</h3>
                        <p className="text-sm text-muted-foreground flex items-center gap-1">
                          <MapPin size={12} /> {branch.location}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-4 text-sm text-muted-foreground ml-13">
                      <span className="flex items-center gap-1"><Clock size={14} /> {branch.office_start_time} ({branch.timezone})</span>
                      <span>Grace: {branch.grace_minutes} min</span>
                      <span>{branch.lates_per_day_deduction} lates = 1 day deduction</span>
                      <span>{branch.employee_count || 0} employees</span>
                    </div>
                  </div>
                  {isSuperUser && (
                    <div className="flex gap-2 shrink-0">
                      <Button variant="outline" size="sm" onClick={() => handleEdit(branch)} data-testid={`edit-branch-${branch.id}`}>
                        <Pencil size={14} />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleDelete(branch.id)} className="text-destructive hover:text-destructive">
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">{editBranch ? 'Edit Branch' : 'Create Branch'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Branch Name</Label>
              <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="e.g., VisaXpress Delhi" data-testid="branch-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Location</Label>
              <Input value={form.location} onChange={e => setForm({...form, location: e.target.value})} placeholder="e.g., New Delhi, India" data-testid="branch-location-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Timezone</Label>
                <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={form.timezone} onChange={e => setForm({...form, timezone: e.target.value})} data-testid="branch-timezone-select">
                  <option value="Asia/Kolkata">Asia/Kolkata (IST)</option>
                  <option value="America/Toronto">America/Toronto (EST)</option>
                  <option value="America/New_York">America/New_York (EST)</option>
                  <option value="Europe/London">Europe/London (GMT)</option>
                  <option value="Australia/Sydney">Australia/Sydney (AEST)</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label>Office Start Time</Label>
                <Input type="time" value={form.office_start_time} onChange={e => setForm({...form, office_start_time: e.target.value})} data-testid="branch-start-time-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Grace Period (minutes)</Label>
                <Input type="number" value={form.grace_minutes} onChange={e => setForm({...form, grace_minutes: parseInt(e.target.value) || 0})} data-testid="branch-grace-input" />
              </div>
              <div className="space-y-2">
                <Label>Lates per Day Deduction</Label>
                <Input type="number" value={form.lates_per_day_deduction} onChange={e => setForm({...form, lates_per_day_deduction: parseInt(e.target.value) || 1})} data-testid="branch-lates-deduction-input" />
                <p className="text-xs text-muted-foreground">{form.lates_per_day_deduction} lates = 1 day salary deduction</p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} data-testid="branch-save-button">{editBranch ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
